import React, { FC, memo } from 'react';
import cn from 'classnames';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import get from 'lodash/get';
import SuccessDialog from './success-dialog';
import IconExcel from '@views/static/excel.svg';
import IconPdf from '@views/static/pdf.svg';
import { absoluteUrl } from '@lib/helpers';

import {
  FormGroup,
  FormControl,
  Row,
  Col,
  Spinner,
  Tooltip,
  OverlayTrigger,
} from 'react-bootstrap';

import {
  EVENT_CLASS_NAME,
  EVENT_ACTION_NAME,
  EVENT_CATEGORY,
  EVENT_LABEL,
} from '@helpers/events';

import {
  Wrapper,
  FormControlStyled,
  ButtonUpload,
  HelperUploadText,
  PreviewWrapper,
  ErrorText,
  Subtitle,
  SubmitButton,
  HelperTitle,
} from './styled';

import QuotationProduct from '../products';
import { useUpload, extname, MAX_FILE_UPLOAD } from './hook';

const INITIAL_VALUES: {
  name: string;
  phone: string;
  email: string;
} = {
  name: '',
  phone: '',
  email: '',
};

const VALIDATION_SCHEMA = Yup.object().shape({
  name: Yup.string().required('Vui lòng nhập tên'),
  email: Yup.string()
    // .required('Vui lòng nhập email')
    .email('Địa chỉ email không hợp lệ'),
  phone: Yup.string()
    .required('Vui lòng nhập số điện thoại liên hệ')
    .matches(
      /((09|03|07|08|05)+([0-9]{8})\b)/,
      'Số điện thoại phải bắt đầu 09,03,07,08,05 và 10 số'
    )
    .min(10, 'Số điện thoại phải bắt đầu 09,03,07,08,05 và 10 số')
    .max(10, 'Số điện thoại phải bắt đầu 09,03,07,08,05 và 10 số'),
});

const QuotationForm: FC = () => {
  const {
    loading,
    error,
    files,
    openDialog,
    handleFileChanged,
    handleSubmit,
    handleRemoveFile,
    handleToggleDialog,
  } = useUpload();

  return (
    <Wrapper>
      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={VALIDATION_SCHEMA}
        onSubmit={handleSubmit}
      >
        {({ values, handleSubmit, handleChange, errors, isSubmitting }) => (
          <Row>
            <Col md={4}>
              <img
                src={absoluteUrl('/assets/quotation/q4.jpg')}
                alt="GỬI CHÚNG TÔI DANH SÁCH MUA HÀNG CỦA BẠN, CHÚNG TÔI SẼ PHẢN HỒI NGAY LẬP TỨC"
                width="100%"
              />
            </Col>

            <Col md={8}>
              <div className="mr-md-2 m-md-2">
                <QuotationProduct />
                <Subtitle>Gửi báo giá với file có sẵn</Subtitle>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <HelperTitle>1. Nhập thông tin</HelperTitle>
                      <FormGroup>
                        <FormControlStyled
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          isInvalid={!!errors.name}
                          placeholder="Tên quý khách hàng"
                          disabled={isSubmitting || loading}
                        />
                        <FormControl.Feedback type="invalid">
                          {errors.name}
                        </FormControl.Feedback>
                      </FormGroup>
                      <FormGroup>
                        <FormControlStyled
                          name="phone"
                          type="text"
                          placeholder="Số điện thoại/Zalo"
                          value={values.phone}
                          onChange={handleChange}
                          onKeyPress={(event) => {
                            const re = /[0-9]+/g;
                            const allowKeyCode = [8];
                            if (
                              !re.test(event.key) &&
                              !allowKeyCode.includes(event.keyCode)
                            ) {
                              event.preventDefault();
                            }
                          }}
                          isInvalid={!!errors.phone}
                          disabled={isSubmitting}
                        />
                        <FormControl.Feedback type="invalid">
                          {errors.phone}
                        </FormControl.Feedback>
                      </FormGroup>
                      <FormGroup>
                        <FormControlStyled
                          name="email"
                          value={values.email}
                          onChange={handleChange}
                          isInvalid={!!errors.email}
                          placeholder="Email liên hệ (không bắt buộc)"
                          disabled={isSubmitting || loading}
                        />
                        <FormControl.Feedback type="invalid">
                          {errors.email}
                        </FormControl.Feedback>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      {/* <ButtonContainer> */}
                      <HelperTitle>2. Tải file báo giá</HelperTitle>
                      <div className="mb-4">
                        <FormControl
                          type="file"
                          name="avatar"
                          id="block-file"
                          accept="image/png, image/gif, image/jpeg, application/pdf, .xlsx, .xls, .csv"
                          aria-label="tải ảnh lên"
                          className="d-none"
                          multiple
                          onInput={handleFileChanged}
                          disabled={
                            isSubmitting ||
                            loading ||
                            files.length >= MAX_FILE_UPLOAD
                          }
                        />
                        <ButtonUpload
                          className={cn(
                            EVENT_CLASS_NAME,
                            'btn',
                            'btn-primary',
                            'text-bold',
                            {
                              disabled:
                                isSubmitting ||
                                loading ||
                                files.length >= MAX_FILE_UPLOAD,
                            }
                          )}
                          htmlFor="block-file"
                          disabled={isSubmitting || loading}
                          data-category={EVENT_CATEGORY.QUOTATION_FORM}
                          data-action={EVENT_ACTION_NAME}
                          data-label={EVENT_LABEL.CHOOSE_QUOTATION_FILE}
                        >
                          Chọn File{' '}
                          {files.length > 0 ? `(${files.length})` : null}
                        </ButtonUpload>
                        <HelperUploadText>
                          *Tối đa được {MAX_FILE_UPLOAD} file (gồm hình ảnh,
                          excel hoặc pdf)
                        </HelperUploadText>
                        {!!error && <ErrorText>{error}</ErrorText>}
                        {Array.isArray(files) && (
                          <PreviewWrapper>
                            {/* URL.createObjectURL(file) */}
                            {files.map((file, index) => {
                              const name = get(file, 'name');
                              const key = `${name}${index}`;
                              const ext = extname(name).toLowerCase();
                              switch (ext) {
                                case '.xls':
                                case '.xlsx': {
                                  return (
                                    <li key={key}>
                                      <OverlayTrigger
                                        placement="bottom"
                                        overlay={
                                          <Tooltip id={name}>{name}</Tooltip>
                                        }
                                      >
                                        <IconExcel
                                          width={50}
                                          height={50}
                                          title={name}
                                        />
                                      </OverlayTrigger>

                                      <button
                                        type="button"
                                        className="icon-close"
                                        onClick={() => handleRemoveFile(index)}
                                      />
                                    </li>
                                  );
                                }
                                case '.png':
                                case '.gif':
                                case '.jpg':
                                case '.jpeg': {
                                  return (
                                    <li key={key}>
                                      <OverlayTrigger
                                        placement="bottom"
                                        overlay={
                                          <Tooltip id={name}>{name}</Tooltip>
                                        }
                                      >
                                        <img
                                          src={URL.createObjectURL(file)}
                                          alt={name}
                                          title={name}
                                        />
                                      </OverlayTrigger>

                                      <button
                                        type="button"
                                        className="icon-close"
                                        onClick={() => handleRemoveFile(index)}
                                      />
                                    </li>
                                  );
                                }

                                case '.pdf': {
                                  return (
                                    <li key={key}>
                                      <OverlayTrigger
                                        placement="bottom"
                                        overlay={
                                          <Tooltip id={name}>{name}</Tooltip>
                                        }
                                      >
                                        <IconPdf
                                          width={50}
                                          height={50}
                                          title={name}
                                        />
                                      </OverlayTrigger>
                                      <button
                                        type="button"
                                        className="icon-close"
                                        onClick={() => handleRemoveFile(index)}
                                      />
                                    </li>
                                  );
                                }
                                default: {
                                  return (
                                    <li key={key}>
                                      <div>{ext}</div>

                                      <button
                                        type="button"
                                        className="icon-close"
                                        onClick={() => handleRemoveFile(index)}
                                      />
                                    </li>
                                  );
                                }
                              }
                            })}
                          </PreviewWrapper>
                        )}
                      </div>

                      <HelperTitle>
                        3. Gửi file báo giá đến Sơn Thủy
                      </HelperTitle>
                      <SubmitButton
                        type="submit"
                        variant="primary"
                        className={cn(EVENT_CLASS_NAME, 'text-bold')}
                        disabled={isSubmitting || loading}
                        data-category={EVENT_CATEGORY.QUOTATION_FORM}
                        data-action={EVENT_ACTION_NAME}
                        data-label={EVENT_LABEL.SUBMIT_QUOTATION_FORM}
                      >
                        {isSubmitting || loading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="grow"
                              role="status"
                              aria-hidden="true"
                              size="sm"
                            />
                            &nbsp; Đang gửi..
                          </>
                        ) : (
                          <>GỬI Sơn Thủy</>
                        )}
                      </SubmitButton>
                      {/* </ButtonContainer> */}
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>
          </Row>
        )}
      </Formik>

      <SuccessDialog open={openDialog} onClose={handleToggleDialog} />
    </Wrapper>
  );
};

const Container = memo(QuotationForm);
Container.displayName = 'QuotationForm';

export default Container;
